// Buildings & projects: projects as groups, each building with its stage, PC date, contract (sum, certified, retention)
// and open items per register. Contracts live on the building — the separate Contracts page is gone.
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { money } from '../lib/claimCalc';
import { STAGES, fmtDate, stageLabel } from '../lib/format';
import { isClosed, type ItemType } from '../lib/registers';
import { STAGE_LOOK, STAGE_STEPS, contractsFor, type Building, type Contract, type Project } from '../lib/buildings';
import { Button, Empty, PageHeader, errText, useToast } from '../ui/kit';
import { write } from '../lib/api';
import { RecordDrawer, type FieldSpec } from '../ui/RecordDrawer';
import { ContractEditor } from '../ui/building/ContractEditor';
import { progressOf, useContractClaims } from '../ui/building/ContractsCard';

const NONE = '00000000-0000-0000-0000-000000000000';
type Org = { id: string; name: string; kind: string; short_code: string | null };
type Lite = { id: string; type: ItemType; status: string; amount: number | null; building_ids: string[]; review: unknown[] | null };
const viewKey = (uid: string) => `pmc3.buildings.view.${uid}`;

/** All items on the site (light columns), paged past the 1000-row limit. */
function useSiteItems(siteId: string | null) {
  const [rows, setRows] = useState<Lite[]>([]);
  useEffect(() => {
    if (!siteId) return;
    let stop = false;
    void (async () => {
      const out: Lite[] = [];
      for (let from = 0; from < 20000; from += 1000) {
        const { data } = await supabase.from('items').select('id, type, status, amount, building_ids, review').eq('site_id', siteId).is('deleted_at', null).order('id').range(from, from + 999);
        out.push(...((data ?? []) as Lite[]));
        if (!data || data.length < 1000) break;
      }
      if (!stop) setRows(out);
    })();
    return () => {
      stop = true;
    };
  }, [siteId]);
  return rows;
}

export default function Buildings() {
  const { siteId, site, can, session } = useApp();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const uid = session?.user.id ?? 'anon';
  const [stage, setStage] = useState('all');
  const [q, setQ] = useState('');
  const [view, setView] = useState<'cards' | 'table'>(() => {
    try {
      return localStorage.getItem(viewKey(uid)) === 'table' ? 'table' : 'cards';
    } catch {
      return 'cards';
    }
  });
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const [bOpen, setBOpen] = useState<Building | 'new' | null>(null);
  const [newIn, setNewIn] = useState<string | null>(null);
  const [pOpen, setPOpen] = useState<Project | 'new' | null>(null);
  const [cFor, setCFor] = useState<Building | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  // arrange: a working order per project group (building ids); saved as buildings.sort in one batch
  const [order, setOrder] = useState<Record<string, string[]> | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const toast = useToast();

  // old links: /buildings?open=<id> → the building page
  useEffect(() => {
    const id = params.get('open');
    if (id) nav(`/buildings/${id}`, { replace: true });
  }, [params, nav]);

  const bs = useRows<Building>(() => supabase.from('buildings').select('*').eq('site_id', siteId ?? NONE).order('sort').order('name'), [siteId], ['buildings']);
  const projects = useRows<Project>(() => supabase.from('projects').select('*').eq('site_id', siteId ?? NONE).order('sort').order('name'), [siteId], ['projects']);
  const cs = useRows<Contract>(() => supabase.from('contracts').select('*').eq('site_id', siteId ?? NONE).is('deleted_at', null), [siteId], ['contracts']);
  const orgs = useRows<Org>(() => supabase.from('organisations').select('id, name, kind, short_code').is('deleted_at', null).order('name'), [], ['organisations']);
  const items = useSiteItems(siteId);
  const claimContracts = cs.rows.filter((c) => c.has_claims);
  const claims = useContractClaims(claimContracts.map((c) => c.id));
  const seeC = can('contracts', 1);
  const editB = can('buildings', 4);
  const editC = can('contracts', 4);

  const live = bs.rows.filter((b) => showDeleted || !b.deleted_at);
  const orgShort = (id: string | null) => {
    const o = orgs.rows.find((x) => x.id === id);
    return o ? (o.name.length <= 12 ? o.name : o.short_code ?? o.name) : null;
  };

  const stats = useMemo(() => {
    const by = new Map<string, { rfi: number; notice: number; variation: number; claim: number; review: number; approvedVar: number }>();
    for (const b of bs.rows) by.set(b.id, { rfi: 0, notice: 0, variation: 0, claim: 0, review: 0, approvedVar: 0 });
    for (const i of items)
      for (const id of i.building_ids ?? []) {
        const s = by.get(id);
        if (!s) continue;
        if (i.type === 'rfi' && !isClosed(i.status)) s.rfi++;
        if (i.type === 'notice' && !isClosed(i.status)) s.notice++;
        if (i.type === 'variation') s.variation++;
        if (i.type === 'variation' && (i.status === 'approved' || i.status === 'complete')) s.approvedVar += Number(i.amount ?? 0);
        if (i.type === 'claim') s.claim++;
        if (i.review && i.review.length) s.review++;
      }
    return by;
  }, [items, bs.rows]);
  const unassigned = items.filter((i) => !(i.building_ids ?? []).length || (i.review && i.review.length)).length;

  const arranging = order !== null;
  const shown = arranging ? live.filter((b) => !b.deleted_at) : live.filter((b) => (stage === 'all' || b.stage === stage) && (!q || [b.name, b.short_code, ...(b.aliases ?? [])].join(' ').toLowerCase().includes(q.toLowerCase())));
  const groups = [...projects.rows.filter((p) => !p.deleted_at).map((p) => ({ id: p.id, p })), { id: '', p: null as Project | null }]
    .map((g) => {
      const list = shown.filter((b) => (b.project_id ?? '') === g.id);
      const ids = order?.[g.id];
      return { ...g, list: ids ? ids.map((id) => list.find((b) => b.id === id)).filter((b): b is Building => Boolean(b)) : list };
    })
    .filter((g) => g.p || g.list.length);

  const startArrange = () => {
    const o: Record<string, string[]> = {};
    for (const g of groups) o[g.id] = g.list.filter((b) => !b.deleted_at).map((b) => b.id);
    setStage('all');
    setQ('');
    setOrder(o);
  };
  const move = (gid: string, id: string, to: number) => {
    if (!order) return;
    const ids = order[gid].filter((x) => x !== id);
    ids.splice(Math.max(0, Math.min(ids.length, to)), 0, id);
    setOrder({ ...order, [gid]: ids });
  };
  async function saveOrder() {
    if (!order) return;
    setSavingOrder(true);
    const batch = crypto.randomUUID();
    let n = 0;
    try {
      let k = 0;
      for (const g of groups)
        for (const id of order[g.id] ?? []) {
          k += 10;
          const b = bs.rows.find((x) => x.id === id);
          if (!b || b.sort === k) continue;
          await write('buildings', 'update', { id, batch_id: batch, values: { sort: k }, summary: 'Arranged the buildings list' });
          n++;
        }
      toast({ tone: 'ok', text: n ? `Order saved (${n} building${n === 1 ? '' : 's'} moved) — everyone sees this order` : 'Order unchanged' });
      setOrder(null);
      void bs.reload();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setSavingOrder(false);
    }
  }
  const mover = (gid: string, b: Building, i: number, n: number) =>
    arranging ? (
      <span className="bl-move">
        <span className="bl-grip" aria-hidden="true">
          ⠿
        </span>
        <button type="button" className="bl-mv" aria-label={`Move ${b.name} up`} disabled={i === 0} onClick={() => move(gid, b.id, i - 1)}>
          ↑
        </button>
        <button type="button" className="bl-mv" aria-label={`Move ${b.name} down`} disabled={i === n - 1} onClick={() => move(gid, b.id, i + 1)}>
          ↓
        </button>
      </span>
    ) : null;
  const dragProps = (gid: string, b: Building, i: number) =>
    arranging
      ? {
          draggable: true,
          onDragStart: (e: React.DragEvent) => (setDragId(b.id), e.dataTransfer.setData('text/plain', b.id), (e.dataTransfer.effectAllowed = 'move')),
          onDragOver: (e: React.DragEvent) => {
            if (dragId && order?.[gid]?.includes(dragId)) e.preventDefault();
          },
          onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            if (dragId && dragId !== b.id && order?.[gid]?.includes(dragId)) move(gid, dragId, i);
            setDragId(null);
          },
          onDragEnd: () => setDragId(null),
        }
      : {};

  const underContract = claimContracts.reduce((s, c) => s + Number(c.sum_vep ?? 0), 0);
  const progAll = claimContracts.map((c) => progressOf(c, claims, 0));
  const certAll = progAll.reduce((s, p) => s + p.toDate.gross_vep, 0);
  const retAll = progAll.reduce((s, p) => s + p.toDate.retention_held, 0);
  const without = live.filter((b) => !b.deleted_at && !claimContracts.some((c) => c.building_ids.includes(b.id))).length;

  const bFields = useMemo<FieldSpec[]>(
    () => [
      { key: 'name', label: 'Name', required: true },
      { key: 'short_code', label: 'Short code', hint: 'e.g. TH, SUN, SV' },
      { key: 'aliases', label: 'Other names', type: 'list', hint: 'Comma separated — used to match imports and emails' },
      { key: 'project_id', label: 'Project', type: 'select', options: projects.rows.filter((p) => !p.deleted_at).map((p) => ({ value: p.id, label: p.name })) },
      { key: 'stage', label: 'Stage', type: 'select', options: STAGES, required: true },
      { key: 'pc_date', label: 'Practical completion date', type: 'date' },
      { key: 'revised_pc_date', label: 'Revised PC date (after EOTs)', type: 'date' },
      { key: 'drive_folder_url', label: 'Google Drive folder', type: 'url' },
      { key: 'photos_url', label: 'Google Photos album', type: 'url', hint: 'Shared album link — opens in Google Photos' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    [projects.rows],
  );
  const pFields: FieldSpec[] = [
    { key: 'name', label: 'Project name', required: true },
    { key: 'code', label: 'Code', hint: 'e.g. TAV-P2' },
    { key: 'start_on', label: 'Start', type: 'date' },
    { key: 'notes', label: 'Notes', type: 'textarea' },
  ];

  const setV = (v: 'cards' | 'table') => {
    setView(v);
    try {
      localStorage.setItem(viewKey(uid), v);
    } catch {
      /* not remembered */
    }
  };

  return (
    <div className="bl">
      <PageHeader
        title="Buildings & projects"
        sub={`${site?.name ?? ''} — each building holds every contract and document that applies to it, plus its claims and registers.`}
        actions={
          editB ? (
            <>
              <Button onClick={() => setPOpen('new')}>+ Project</Button>
              <Button kind="primary" onClick={() => (setNewIn(projects.rows[0]?.id ?? null), setBOpen('new'))}>
                + Building
              </Button>
            </>
          ) : null
        }
      />

      <div className="bl-stats">
        <div className="money">
          <span>Buildings</span>
          <b>{live.filter((b) => !b.deleted_at).length}</b>
          <span>
            {projects.rows.filter((p) => !p.deleted_at).length} project{projects.rows.length === 1 ? '' : 's'} · {live.filter((b) => b.stage === 'complete').length} complete
          </span>
        </div>
        {seeC ? (
          <>
            <div className="money">
              <span>Under contract · VEP</span>
              <b>{money(underContract)}</b>
              <span>
                {claimContracts.length} contract{claimContracts.length === 1 ? '' : 's'} with claims · {without} building{without === 1 ? '' : 's'} without one
              </span>
            </div>
            <div className="money">
              <span>Certified to date · VEP</span>
              <b className="good-t">{money(certAll)}</b>
              <span>{underContract ? `${Math.round((certAll / underContract) * 100)} % of contract sums` : 'no contract sums yet'}</span>
            </div>
            <div className="money">
              <span>Retention held · FJD</span>
              <b>{money(retAll)}</b>
              <span>across all contracts</span>
            </div>
          </>
        ) : null}
        <Link className="money review-tile" to="/r/rfis?review=1">
          <span>To be reviewed</span>
          <b>{unassigned}</b>
          <span>items with no building or a name we couldn't match</span>
        </Link>
      </div>

      <div className="bl-filters">
        <div className="chips" role="group" aria-label="Stage">
          {[{ value: 'all', label: 'All' }, ...STAGES].map((s) => {
            const n = s.value === 'all' ? live.length : live.filter((b) => b.stage === s.value).length;
            if (!n && s.value !== 'all') return null;
            return (
              <button key={s.value} type="button" className={`chip ${stage === s.value ? 'on' : ''}`} aria-pressed={stage === s.value} onClick={() => setStage(s.value)}>
                {s.label} <span className="muted">{n}</span>
              </button>
            );
          })}
        </div>
        <span className="grow" />
        <input type="search" placeholder="Name, code or other name" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find a building" />
        <div className="bseg" role="group" aria-label="View">
          <button type="button" className={view === 'cards' ? 'on' : ''} aria-pressed={view === 'cards'} onClick={() => setV('cards')}>
            Cards
          </button>
          <button type="button" className={view === 'table' ? 'on' : ''} aria-pressed={view === 'table'} onClick={() => setV('table')}>
            Table
          </button>
        </div>
        <label className="check small">
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} /> Show deleted
        </label>
        {editB && !arranging ? <Button onClick={startArrange}>Arrange buildings</Button> : null}
      </div>
      {arranging ? (
        <div className="bl-arrange" role="status">
          <b>Arrange buildings</b>
          <span className="grow small">Drag a building, or use ↑ ↓, to set the order within each project. The order is saved for everyone.</span>
          <Button onClick={() => setOrder(null)}>Cancel</Button>
          <Button kind="primary" disabled={savingOrder} onClick={() => void saveOrder()}>
            {savingOrder ? 'Saving…' : 'Save order'}
          </Button>
        </div>
      ) : null}

      {bs.error ? <div className="error-box">{bs.error}</div> : null}
      {bs.loading ? <Empty>Loading…</Empty> : null}

      {groups.map((g) => {
        const isClosedG = closed[g.id];
        const everyB = g.p ? cs.rows.filter((c) => c.covers_all && (!c.building_ids.length || bs.rows.find((b) => b.id === c.building_ids[0])?.project_id === g.id)) : [];
        return (
          <section key={g.id || 'none'} className="bl-group">
            <div className="bl-ph">
              <button type="button" aria-expanded={!isClosedG} aria-label={`Show or hide ${g.p?.name ?? 'buildings without a project'}`} onClick={() => setClosed({ ...closed, [g.id]: !isClosedG })}>
                <svg className={`bchev ${isClosedG ? '' : 'open'}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
              <span className="bl-ptag">{g.p ? 'PROJECT' : 'NO PROJECT'}</span>
              <b>{g.p?.name ?? 'Buildings without a project'}</b>
              <span className="small on-dark-muted">
                {g.list.length} building{g.list.length === 1 ? '' : 's'}
                {g.p?.code ? ` · ${g.p.code}` : ''}
              </span>
              <span className="grow" />
              {seeC && everyB.length ? (
                <>
                  <span className="small on-dark-muted">On every building:</span>
                  {everyB.map((c) => (
                    <span key={c.id} className="bl-pchip">
                      {c.title ?? 'Contract'}
                      {orgShort(c.contractor_org_id) ? ` · ${orgShort(c.contractor_org_id)}` : ''}
                    </span>
                  ))}
                </>
              ) : null}
              {g.p && editB ? (
                <>
                  <button type="button" className="bl-plink" onClick={() => (setNewIn(g.id), setBOpen('new'))}>
                    + Building
                  </button>
                  <button type="button" className="bl-plink" onClick={() => setPOpen(g.p)}>
                    Edit project
                  </button>
                </>
              ) : null}
            </div>
            {isClosedG ? null : (
              <div className="bl-body">
                {g.list.length === 0 ? <p className="muted small" style={{ margin: 0 }}>{stage === 'all' && !q ? 'No buildings in this project yet.' : 'No buildings match.'}</p> : null}
                {view === 'cards' ? (
                  <div className="bl-cards">
                    {g.list.map((b, bi) => {
                      const s = stats.get(b.id);
                      const look = STAGE_LOOK[b.stage] ?? STAGE_LOOK.design;
                      const mine = contractsFor(b, cs.rows, bs.rows);
                      const main = mine.find((c) => c.has_claims && c.building_ids.includes(b.id));
                      const p = main ? progressOf(main, claims, s?.approvedVar ?? 0) : null;
                      const others = mine.length - (main ? 1 : 0);
                      return (
                        <div key={b.id} className={`bl-card ${b.deleted_at ? 'deleted' : ''} ${arranging ? 'arranging' : ''} ${dragId === b.id ? 'dragging' : ''}`} {...dragProps(g.id, b, bi)}>
                          {arranging ? null : <Link to={`/buildings/${b.id}`} className="bl-cover" aria-label={`Open ${b.name}`} />}
                          {mover(g.id, b, bi, g.list.length)}
                          <div className="bl-top">
                            <span className="bl-code">{b.short_code ?? '—'}</span>
                            <b className="grow">{b.name}</b>
                            {s?.review ? <span className="brv">{s.review} to review</span> : null}
                          </div>
                          <div className="bl-stage">
                            <span className="bstg" style={{ background: look.tint, color: look.color }}>
                              {stageLabel(b.stage)}
                            </span>
                            <span className="bmd" aria-hidden="true">
                              {STAGE_STEPS.map((x, i) => (
                                <span key={x.value} className={look.i < 0 ? '' : i < look.i || b.stage === 'complete' ? 'd' : i === look.i ? 'c' : ''} />
                              ))}
                            </span>
                            <span className="grow" />
                            <span className="small muted">{b.revised_pc_date ? `PC ${fmtDate(b.revised_pc_date)} (rev.)` : b.pc_date ? `PC ${fmtDate(b.pc_date)}` : b.stage === 'complete' ? 'Complete' : 'PC date not set'}</span>
                          </div>
                          {seeC ? (
                            main ? (
                              <div className="bl-con">
                                <div className="bl-con-h">
                                  <span className="k grow">Budget no. {main.contract_no ?? '—'}</span>
                                  {others ? <span className="bpill">+{others} more</span> : null}
                                  {orgShort(main.contractor_org_id) ? <span className="bpill">{orgShort(main.contractor_org_id)}</span> : null}
                                  {orgShort(main.architect_org_id) ? <span className="bpill">{orgShort(main.architect_org_id)}</span> : null}
                                </div>
                                <div className="bl-con-sum">
                                  <b>{main.sum_vep ? money(main.sum_vep) : 'sum to enter'}</b>
                                  {main.sum_vep ? <span className="small muted">VEP</span> : null}
                                  <span className="grow" />
                                  <span className="small muted">{Math.round(p!.certifiedPct)} % certified</span>
                                </div>
                                <span className="track" role="img" aria-label={`${Math.round(p!.certifiedPct)} % certified`}>
                                  <i style={{ width: `${p!.certifiedPct}%` }} />
                                </span>
                                <div className="bl-con-f small muted">
                                  <span>{p!.lastCertified ? `Claim ${p!.lastCertified} certified` : 'No claims certified yet'}</span>
                                  <span>Retention {money(p!.toDate.retention_held)}</span>
                                </div>
                              </div>
                            ) : (
                              <div className="bl-con none">
                                <span>{others ? `${others} shared contract${others === 1 ? '' : 's'} · no building contract` : 'No contract yet'}</span>
                                {editC ? (
                                  <button type="button" className="linkbtn strong" onClick={() => setCFor(b)}>
                                    + Add contract
                                  </button>
                                ) : null}
                              </div>
                            )
                          ) : null}
                          <div className="bl-counts">
                            <Link to={`/r/rfis?b=${b.id}`} className="bcnt rfi">
                              <i />
                              {s?.rfi ?? 0} RFI{s?.rfi === 1 ? '' : 's'} open
                            </Link>
                            <Link to={`/r/notices?b=${b.id}`} className="bcnt notice">
                              <i />
                              {s?.notice ?? 0} notice{s?.notice === 1 ? '' : 's'} open
                            </Link>
                            <Link to={`/r/variations?b=${b.id}`} className="bcnt var">
                              <i />
                              {s?.variation ?? 0} var. &amp; req.
                            </Link>
                            <Link to={`/r/claims?b=${b.id}`} className="bcnt claim">
                              <i />
                              {s?.claim ?? 0} claim{s?.claim === 1 ? '' : 's'}
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : g.list.length ? (
                  <div className="table-wrap">
                    <table className="t bl-table">
                      <thead>
                        <tr>
                          {arranging ? <th>Order</th> : null}
                          <th>Code</th>
                          <th>Building</th>
                          <th>Stage</th>
                          <th>PC date</th>
                          {seeC ? (
                            <>
                              <th>Budget no.</th>
                              <th className="right">Sum VEP</th>
                              <th className="right">Certified</th>
                              <th className="right">Retention</th>
                            </>
                          ) : null}
                          <th className="right">RFIs open</th>
                          <th className="right">Var. &amp; req.</th>
                          <th className="right">Claims</th>
                          <th>Review</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.list.map((b, bi) => {
                          const s = stats.get(b.id);
                          const look = STAGE_LOOK[b.stage] ?? STAGE_LOOK.design;
                          const main = contractsFor(b, cs.rows, bs.rows).find((c) => c.has_claims && c.building_ids.includes(b.id));
                          const p = main ? progressOf(main, claims, s?.approvedVar ?? 0) : null;
                          return (
                            <tr key={b.id} className={`${b.deleted_at ? 'deleted' : ''} ${dragId === b.id ? 'dragging' : ''}`} {...dragProps(g.id, b, bi)}>
                              {arranging ? <td>{mover(g.id, b, bi, g.list.length)}</td> : null}
                              <td>
                                <b>{b.short_code ?? '—'}</b>
                              </td>
                              <td>
                                <Link to={`/buildings/${b.id}`}>{b.name}</Link>
                              </td>
                              <td>
                                <span className="bstg" style={{ background: look.tint, color: look.color }}>
                                  {stageLabel(b.stage)}
                                </span>
                              </td>
                              <td>{fmtDate(b.revised_pc_date ?? b.pc_date)}</td>
                              {seeC ? (
                                <>
                                  <td>{main?.contract_no ?? '—'}</td>
                                  <td className="right">{main?.sum_vep ? money(main.sum_vep) : '—'}</td>
                                  <td className="right">{p ? `${Math.round(p.certifiedPct)} %` : '—'}</td>
                                  <td className="right">{p ? money(p.toDate.retention_held) : '—'}</td>
                                </>
                              ) : null}
                              <td className="right">{s?.rfi ?? 0}</td>
                              <td className="right">{s?.variation ?? 0}</td>
                              <td className="right">{s?.claim ?? 0}</td>
                              <td>{s?.review ? <span className="brv">{s.review}</span> : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            )}
          </section>
        );
      })}

      <RecordDrawer
        open={bOpen !== null}
        onClose={() => setBOpen(null)}
        title={bOpen === 'new' ? 'Add building' : bOpen ? bOpen.name : ''}
        table="buildings"
        fields={bFields}
        record={bOpen === 'new' ? null : bOpen}
        defaults={{ site_id: siteId, stage: 'design', project_id: newIn }}
        canEdit={editB}
        onSaved={(row) => {
          void bs.reload();
          if (bOpen === 'new' && row.id) nav(`/buildings/${row.id}`);
        }}
        label={(r) => `building ${String(r.name ?? '')}`}
      />
      <RecordDrawer
        open={pOpen !== null}
        onClose={() => setPOpen(null)}
        title={pOpen === 'new' ? 'Add project' : pOpen ? pOpen.name : ''}
        table="projects"
        fields={pFields}
        record={pOpen === 'new' ? null : (pOpen as unknown as Record<string, unknown> & { id: string })}
        defaults={{ site_id: siteId, status: 'active' }}
        canEdit={editB}
        onSaved={() => void projects.reload()}
        label={(r) => `project ${String(r.name ?? '')}`}
      />
      {cFor ? (
        <ContractEditor
          open
          contract={null}
          building={cFor}
          buildings={bs.rows}
          orgs={orgs.rows}
          siteId={siteId!}
          canEdit={editC}
          onClose={() => setCFor(null)}
          onSaved={() => {
            setCFor(null);
            void cs.reload();
          }}
        />
      ) : null}
    </div>
  );
}
